import { css } from '@emotion/react';
import { bindActionCreators } from '@reduxjs/toolkit';
import { space } from 'folio-common-components';
import { colors } from 'folio-design-tokens';
import * as React from 'react';
import { connect } from 'react-redux';
import { InfoIcon, PlusIcon } from '../../icons';
import type { Dispatch, RootState } from '../../state';
import {
  type Person,
  actions as editorActions,
  selectors as editorSelectors,
} from '../../state/editor';
import { nameFallback } from '../../utils';
import { PersonRoleItem } from '../PersonRoleItem';
import { Button } from '../buttons';
import { CeoPersonForm } from './CeoPersonForm';

interface Props {
  ceo: Person | undefined;
  onMakeCeo: (id: string | undefined) => any;
}

interface State {
  mode: 'closed' | 'edit' | 'new';
}

class BaseEditableCeoSummary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { mode: 'closed' };
  }

  closedView() {
    const { ceo, onMakeCeo } = this.props;
    return (
      <PersonRoleItem
        name={ceo ? nameFallback(ceo.name) : 'Ingen daglig leder valgt'}
        // eslint-disable-next-line jsx-a11y/aria-role
        role="ceo"
        onEdit={() => {
          this.setState({ mode: 'edit' });
        }}
        onDelete={() => {
          onMakeCeo(undefined);
        }}
        actions={[
          {
            label: 'Endre daglig leder',
            onClick: () => {
              this.setState({ mode: 'new' });
            },
          },
        ]}
      />
    );
  }

  editView() {
    return (
      <CeoPersonForm
        currentCeo={this.props.ceo}
        onDone={() => this.setState({ mode: 'closed' })}
        editType="edit"
      />
    );
  }

  newView() {
    return (
      <CeoPersonForm
        currentCeo={this.props.ceo}
        onDone={() => this.setState({ mode: 'closed' })}
        editType="new"
      />
    );
  }

  noCeoSelectedView() {
    return (
      <>
        <div
          css={css`
            display: flex;
            align-items: center;
            ${space([8], 'gap')};
            ${space([16], 'margin-bottom')};
          `}
        >
          <div
            css={css`
              flex: 0;
            `}
          >
            <InfoIcon
              color={colors.blue}
              css={css`
                display: block;
              `}
            />
          </div>
          <div>
            Husk at styreleder må bestille bankkonto, hvis det ikke er noen
            daglig leder.
          </div>
        </div>
        <Button
          level="secondary"
          onClick={() => this.setState({ mode: 'new' })}
          icon={
            <PlusIcon
              css={css`
                margin-top: -2px;
              `}
            />
          }
        >
          Legg til daglig leder
        </Button>
      </>
    );
  }

  modeView() {
    const { mode } = this.state;
    const { ceo } = this.props;
    if (mode === 'edit') {
      return this.editView();
    }

    if (mode === 'new') {
      return this.newView();
    }

    if (ceo === undefined) {
      return this.noCeoSelectedView();
    }

    return this.closedView();
  }

  render() {
    return (
      <>
        <p>Ansvarlig for daglig drift av selskapet.</p>

        {this.modeView()}
      </>
    );
  }
}

function mapStateToProps(state: RootState) {
  return {
    ceo: editorSelectors.getCeo(state),
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators(
    {
      onMakeCeo: (id: string | undefined) => editorActions.assignCeo(id),
    },
    dispatch,
  );
}

export const EditableCeoSummary = connect(
  mapStateToProps,
  mapDispatchToProps,
)(BaseEditableCeoSummary);
